/**
 * MTGJSONMetadataDisplay component
 * Shows MTGJSON version and build date in a small, unobtrusive format.
 * A metadata failure keeps this footer block visible with a short error.
 */

import React, { useEffect, useState } from 'react';
import { fetchMTGJSONMetadata, type MTGJSONMetadata } from '../../api/mtgjson';
import { colors, spacing, typographyFlat } from '../../styles/tokens';
import { formatInstant, formatRelativeTime } from '../../utils/datetime';

const RELATIVE_BUILD = /^(Just now|\d+[mhd] ago)$/;

/** Absolute build time always includes a zone; a recent build also shows a relative hint. */
function formatCatalogBuild(date: string): string {
  const absolute = formatInstant(date);
  const relative = formatRelativeTime(date, { fallback: absolute });
  if (relative === absolute || !RELATIVE_BUILD.test(relative)) {
    return absolute;
  }
  return `${absolute} (${relative})`;
}

export const MTGJSONMetadataDisplay: React.FC = () => {
  const [metadata, setMetadata] = useState<MTGJSONMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const data = await fetchMTGJSONMetadata();
        setMetadata(data);
        setError(null);
      } catch (err) {
        console.error('Failed to load MTGJSON metadata:', err);
        setError('unavailable');
      } finally {
        setLoading(false);
      }
    };

    loadMetadata();

    // Refresh every 5 minutes
    const interval = setInterval(loadMetadata, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return null;
  }

  const styles: Record<string, React.CSSProperties> = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.xs,
      padding: spacing.sm,
      paddingBottom: spacing.md,
      fontSize: typographyFlat.fontSize.xs,
      color: colors.textSecondary,
      borderBottom: `1px solid ${colors.border}`,
    },
    label: {
      fontWeight: 500,
      fontSize: typographyFlat.fontSize.xs,
      color: colors.textSecondary,
    },
    value: {
      fontFamily: 'ui-monospace, monospace',
      fontSize: '11px',
      color: colors.textSecondary,
    },
    error: {
      fontFamily: 'ui-monospace, monospace',
      fontSize: '11px',
      color: colors.error,
    },
  };

  const version = metadata?.version;
  const showSuccess = !error && Boolean(version);

  return (
    <div style={styles.container} data-testid="mtgjson-metadata">
      <div style={styles.label}>MTGJSON Data</div>
      {showSuccess && metadata ? (
        <>
          <div style={styles.value}>v{metadata.version}</div>
          {metadata.date ? (
            <div style={styles.value}>{formatCatalogBuild(metadata.date)}</div>
          ) : null}
        </>
      ) : (
        <div style={styles.error} role="status">
          Catalog unavailable
        </div>
      )}
    </div>
  );
};
